import React, { useRef, useEffect, useState } from 'react';
import useOnClickOutside from '@hooks/use-on-click-outside';
import vars from '@variables';
import { EMFResponsiveLogoAtom } from '@atoms/logo-atoms/logo-atoms';
import {
  ExploreLink,
  IconWrapper,
  MobileNavWrapper,
  NavContentContainer,
  NavFullWidthContainer,
  StyledNav,
  TabletAndDesktopWrapper,
} from './navbar-block.styles';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { Link } from 'gatsby';
import {
  MobileCloseIcon,
  MobileMenuButton,
  SearchIconAtom,
} from '@atoms/icon-atoms/nav-icon-atoms';
import { MobileMenu } from '@components/mobile-menu/mobile-menu';
import { NavbarLanguageButton } from '@components/navbar-language-button/navbar-language-button';
import { NavbarTabs } from '@components/navbar-tabs/navbar-tabs';
import { NavbarProps } from './navbar-block.types';
import { useNavbarData } from '@hooks/use-navbar-data';
import { useScroll } from 'framer-motion';

export const Navbar: React.FC<NavbarProps> = ({
  locale,
  initialNoBackground,
  initialLightText,
  translate = true,
  navbarLocaleLinks = [],
}) => {
  const [selectedTab, setSelectedTab] = useState(null);
  const [isLanguagesOpen, setIsLanguagesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    initialNoBackground ? 'transparent' : 'white'
  );
  const [textColor, setTextColor] = useState(
    initialLightText ? vars.white : vars.coreDarkGrey
  );

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setSelectedTab(null));

  const { scrollY } = useScroll();

  const handleHidingNavbar = () => {
    if (selectedTab || mobileMenuOpen) {
      return setHidden(false);
    }
    if (scrollY.get() <= 100) {
      setBackgroundColor(initialNoBackground ? 'transparent' : vars.white);
      setTextColor(initialLightText ? vars.white : vars.coreDarkGrey);
    }
    if (scrollY.get() > 100) {
      setBackgroundColor(vars.white);
      setTextColor(vars.coreDarkGrey);
    }

    if (window.innerWidth >= 768) {
      if (scrollY.get() < scrollY.getPrevious()) {
        return setHidden(false);
      } else if (scrollY.get() > 400 && scrollY.get() > scrollY.getPrevious()) {
        return setHidden(true);
      }
    }
  };

  useEffect(() => {
    scrollY.on('change', () => handleHidingNavbar());
  });

  let columnData;

  const navbarData = useNavbarData(locale);

  if (navbarData && navbarData?.navtabs) {
    columnData =
      navbarData.navtabs.find(tab => tab.contentful_id === selectedTab)
        ?.columns || null;
  }

  const handleClick = e => {
    const clickedTab = e.currentTarget.value;
    setIsLanguagesOpen(false);
    setSelectedTab(clickedTab);

    if (selectedTab === clickedTab) {
      setSelectedTab(null);
    }
  };

  const handleLanguagesClick = () => {
    setSelectedTab(null);
    setIsLanguagesOpen(!isLanguagesOpen);
  };

  const handleBackClick = () => {
    setIsLanguagesOpen(false);
    setSelectedTab(null);
  };

  const handleReset = () => {
    setSelectedTab(null);
    setMobileMenuOpen(false);
    setIsLanguagesOpen(false);
  };

  const setExploreLink = () => {
    if (locale === 'pt') return '/pt/saiba-mais';
    if (locale === 'es') return '/es/explora';
    return '/explore';
  };

  const hasTransparentBackground = backgroundColor === 'transparent';

  const setNavbarBackground = () => {
    if (selectedTab || isLanguagesOpen) {
      return vars.white;
    }
    if (backgroundColor) {
      return backgroundColor;
    }
    return vars.white;
  };

  const setEmfLogoColor = () => {
    if (selectedTab || isLanguagesOpen || mobileMenuOpen) {
      return vars.coreDarkGrey;
    }

    return textColor;
  };

  const setHideBorders = () => {
    if (selectedTab || isLanguagesOpen) {
      return false;
    }
    return hasTransparentBackground;
  };

  const setHomepageLink = () => {
    if (locale === 'pt') return '/pt';
    if (locale === 'es') return '/es';
    if (locale === 'zh') return '/zh/overview';
    return '/';
  };

  const renderNavbar = () => {
    if (!navbarData || !navbarData.navtabs) {
      return;
    }
    return (
      <>
        <NavbarTabs
          selectedTab={selectedTab}
          tabs={navbarData.navtabs}
          handleClick={handleClick}
          columnData={columnData}
          footerData={navbarData.footerLinks.footerLinks}
          handleReset={handleReset}
          textColor={textColor}
          isLanguagesOpen={isLanguagesOpen}
          translate={translate}
        />
        <NavbarLanguageButton
          locale={locale}
          handleClick={handleLanguagesClick}
          isExpanded={isLanguagesOpen}
          languages={navbarData.languages.languages}
          hasTransparentBackground={hasTransparentBackground}
          textColor={textColor}
          selectedTab={selectedTab}
          navbarLocaleLinks={navbarLocaleLinks}
        />
      </>
    );
  };

  return (
    <StyledNav
      ref={ref}
      variants={{
        visible: { y: 0 },
        hidden: { y: -57 },
      }}
      animate={hidden ? 'hidden' : 'visible'}
      transition={{ ease: [0.1, 0.25, 0.3, 1], duration: 0.2 }}
    >
      <NavFullWidthContainer
        backgroundColor={setNavbarBackground()}
        hideBorders={setHideBorders()}
        mobileMenuOpen={mobileMenuOpen}
      >
        <NavContentContainer>
          <Link
            to={setHomepageLink()}
            data-testid="nav-bar-home-link"
            data-gtm-id={GTM_ID.NAV_BAR_HOME_LINK}
            onClick={handleReset}
          >
            <EMFResponsiveLogoAtom
              fill={setEmfLogoColor()}
              width={'150px'}
              height={'10px'}
            />
          </Link>
          <TabletAndDesktopWrapper role="tablist">
            {renderNavbar()}
            <ExploreLink
              to={setExploreLink()}
              tabIndex={selectedTab ? -1 : 0}
              data-testid="search-nav-link"
              data-gtm-id={GTM_ID.SEARCH_NAV_LINK}
              hideBorders={setHideBorders()}
              onClick={handleReset}
            >
              <SearchIconAtom width="26" height="26" fill={setEmfLogoColor()} />
            </ExploreLink>
          </TabletAndDesktopWrapper>
          <MobileNavWrapper>
            <ExploreLink
              to={setExploreLink()}
              data-testid="search-mobile-nav-link"
              data-gtm-id={GTM_ID.SEARCH_NAV_LINK}
            >
              <SearchIconAtom
                width="26"
                height="26"
                fill={mobileMenuOpen ? vars.coreDarkGrey : setEmfLogoColor()}
              />
            </ExploreLink>
            <IconWrapper
              onClick={() => setMobileMenuOpen(mobileMenuOpen ? false : true)}
              isOpen={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <MobileCloseIcon />
              ) : (
                <MobileMenuButton stroke={setEmfLogoColor()} />
              )}
            </IconWrapper>
          </MobileNavWrapper>
        </NavContentContainer>
      </NavFullWidthContainer>

      {mobileMenuOpen && (
        <MobileMenu
          selectedTab={selectedTab}
          tabs={navbarData.navtabs}
          columnData={columnData}
          footerData={navbarData.footerLinks.footerLinks}
          handleClick={handleClick}
          handleBackClick={handleBackClick}
          languages={navbarData.languages.languages}
          handleReset={handleReset}
          isLanguagesOpen={isLanguagesOpen}
          handleLanguagesClick={handleLanguagesClick}
          locale={locale}
        />
      )}
    </StyledNav>
  );
};
